import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { PROPERTY_TYPES } from "@rentiohq/shared-frontend/dist/reduxV2/property/property.utils";
import {
  EPropertyMemberTypes,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { join } from "@rentiohq/shared-frontend/dist/utils/string.utils";
import { Grid, Icon, TAssetSource } from "@rentiohq/web-shared/dist/components";
import { sortBy } from "lodash";
import { ForRentSwitch } from "scenes/Properties/components/ForRentSwitch";
import {
  BedIcon,
  RENTMATCH_BRAND_COLOR,
  RentMatchPill,
} from "./PropertyMeta.styles";

// Status badges for the RentMatch pill: a check (published) or a horizontal
// bar (not published), both a brand-colored glyph inside a white circle so
// they stand out on the pink pill.
const RentMatchPublishedBadge = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <circle cx="8" cy="8" r="8" fill="#fff" />
    <path
      d="M4.5 8.4 L6.8 10.7 L11.5 5.4"
      fill="none"
      stroke={RENTMATCH_BRAND_COLOR}
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RentMatchUnpublishedBadge = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
    <circle cx="8" cy="8" r="8" fill="#fff" />
    <line
      x1="4"
      y1="8"
      x2="12"
      y2="8"
      stroke={RENTMATCH_BRAND_COLOR}
      strokeWidth="1.9"
      strokeLinecap="round"
    />
  </svg>
);

interface IProps {
  property: IProperty;
  onForRentSelected?: () => void;
  /** Number of bedrooms (resolved from amenities by the parent). */
  roomCountBedroom?: number;
  /** Whether the broker has the RentMatch feature enabled. */
  hasRentMatch?: boolean;
  /** Opens the RentMatch settings; when omitted the pill is a status only. */
  onRentMatchSettingsClick?: () => void;
}

export const PropertyMeta = (props: IProps) => {
  const {
    property,
    onForRentSelected,
    roomCountBedroom,
    hasRentMatch,
    onRentMatchSettingsClick,
  } = props;

  const propertyType = PROPERTY_TYPES.find(
    (type: any) => type.id === property.typeId,
  );
  const propertyPrice = property?.price
    ? formatCurrency(property.price)
    : undefined;
  const propertyCosts = property?.costs
    ? formatCurrency(property.costs)
    : undefined;
  const propertyAvailableAt = property?.availableAtDate
    ? formatDate(property.availableAtDate, "dd/MM/''yy")
    : undefined;
  const isRentMatchPublished = !!property?.isRentMatchPublished;

  const propertyOwners = property.members
    ? join(
        sortBy(
          property.members
            .filter(member => member.roles.includes(EPropertyMemberTypes.Owner))
            .map(member => getName(member.account)),
          "account.id",
        ),
      )
    : undefined;

  return (
    <Grid alignItems="center">
      <ForRentSwitch
        propertyId={property.id}
        forRentStatus={property.forRentStatus}
        isRentMatchPublished={property.isRentMatchPublished}
        onForRentSelected={onForRentSelected}
      />

      {propertyType && (
        <Grid.Item>
          <Grid spacing="extraTight" alignItems="center">
            <Grid.Item>
              <Icon source={propertyType.icon as TAssetSource} size="small" />
            </Grid.Item>
            <Grid.Item>
              {getLocalizedText(
                `property.type.${propertyType.label}`.toLowerCase(),
              )}
            </Grid.Item>
          </Grid>
        </Grid.Item>
      )}

      {!!propertyOwners && (
        <Grid.Item>
          <Grid spacing="extraTight" alignItems="center">
            <Grid.Item>
              <Icon source="broker" size="small" />
            </Grid.Item>
            <Grid.Item>{propertyOwners}</Grid.Item>
          </Grid>
        </Grid.Item>
      )}

      {propertyPrice && (
        <Grid.Item>
          <Grid spacing="extraTight" alignItems="center">
            <Grid.Item>
              <Icon source="currencyEuroCircle" size="small" />
            </Grid.Item>
            <Grid.Item>{propertyPrice}</Grid.Item>
          </Grid>
        </Grid.Item>
      )}

      {propertyCosts && (
        <Grid.Item>
          <Grid spacing="extraTight" alignItems="center">
            <Grid.Item>
              <Icon source="houseWrench" size="small" />
            </Grid.Item>
            <Grid.Item>{propertyCosts}</Grid.Item>
          </Grid>
        </Grid.Item>
      )}

      {!!propertyAvailableAt && (
        <Grid.Item>
          <Grid spacing="extraTight" alignItems="center">
            <Grid.Item>
              <Icon source="calendar" size="small" />
            </Grid.Item>
            <Grid.Item>{propertyAvailableAt}</Grid.Item>
          </Grid>
        </Grid.Item>
      )}

      {roomCountBedroom != null && (
        <Grid.Item>
          <Grid spacing="extraTight" alignItems="center">
            <Grid.Item>
              <BedIcon viewBox="0 0 24 24" aria-hidden="true">
                <path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8" />
                <path d="M4 10V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4" />
                <path d="M12 4v6" />
                <path d="M2 18h20" />
              </BedIcon>
            </Grid.Item>
            <Grid.Item>{roomCountBedroom}</Grid.Item>
          </Grid>
        </Grid.Item>
      )}

      {hasRentMatch && (
        <Grid.Item>
          <RentMatchPill
            disabled={!onRentMatchSettingsClick}
            onClick={onRentMatchSettingsClick}
            title={getLocalizedText(
              isRentMatchPublished
                ? "property_info.form.is_rent_match_published.pill.published"
                : "property_info.form.is_rent_match_published.pill.not_published",
            )}
          >
            {isRentMatchPublished ? (
              <RentMatchPublishedBadge />
            ) : (
              <RentMatchUnpublishedBadge />
            )}
            Rentmatch
          </RentMatchPill>
        </Grid.Item>
      )}
    </Grid>
  );
};
